import { USERS_SERVICE } from "@/services/users.service.js";
import { ENVIRONMENT_HELPER } from "@/helpers/environment.js";
import store from '@/store.js'
import i18n from '@/helpers/i18n';
import { authHeader } from "@/helpers/auth-header.js";
import { tokenNeedsRefresh } from "@/helpers/token-expiry.js";

export const IS_LOADING_STATUS = "loading";

export const API_HELPER = {
    abortController: new AbortController(),
    pathJoin,
    loggedApiCall,
    apiCall
};

function pathJoin(parts)
{
    return parts.map((part, i) => 
    {
        if (i === 0) {
          return part.toString().trim().replace(/[\/]*$/g, '')
        } else {
          return part.toString().trim().replace(/(^[\/]*|[\/]*$)/g, '')
        }
    }).filter(x=>x.length).join('/');
}

// The refresh in flight, shared by every call made while it runs.
var pendingRefresh = null;

function refreshToken()
{
    if(!pendingRefresh)
    {
        pendingRefresh = USERS_SERVICE.refresh()
        .catch((error) =>
        {
            store.dispatch('users/logout', null, { root: true });

            return Promise.reject("Couldn't refresh the token. " + error);
        })
        .finally(() => { pendingRefresh = null; });
    }

    return pendingRefresh;
}

/* Handles 1) request cancellation 2) refresh the token if the user is not logged in 3) fetch url 4) error handler */
function loggedApiCall(url, queryParams, requestOptions, canBeAborted)
{
    // Validate parameters
    if(!url || !requestOptions)
    {
        throw("The necessary info for the api call was not provided!");
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(requestOptions) }

    canBeAborted = (canBeAborted === undefined || canBeAborted === null) ? true : canBeAborted;

    // Validate if the user is logged in.
    let user = JSON.parse(localStorage.getItem('user'));
    if(!user || !user.data.accessToken)
    {
        store.dispatch('users/logout', null, { root: true });
        return Promise.reject("The user is not logged in.");
    }

    // Refresh the access token BEFORE the call when it expires within the buffer, then send the call with the new token.
    // One refresh at a time: the API rotates the refresh token, so a second refresh with the same token would 404 (logout).
    var tokenReady = tokenNeedsRefresh(user.data.expiration)
        ? refreshToken().then(() => {
            requestOptions.headers = { ...requestOptions.headers, ...authHeader() };
        })
        : Promise.resolve();

    // Build query parameters
    var fullUrl = encodeURI(url);
    if(queryParams)
    {
        fullUrl += "?";
        for(var key in queryParams) 
        {
            var value = queryParams[key];

            if(value || value == 0)
            {
                var encodedValue = encodeURIComponent(value);

                fullUrl += `&${key}=${encodedValue}`;
            }
        }
    }
   
    // Complete the request options adding signal so that the request can be aborted.
    if(canBeAborted)
    {
        requestOptions.signal = API_HELPER.abortController.signal;
    }

    // Add current language to request
    if(!requestOptions.headers) 
    {
        requestOptions.headers = {};
    }
    requestOptions.headers["Accept-Language"] = i18n.locale;

    if(ENVIRONMENT_HELPER.isDev()) { console.log(fullUrl) }
    return tokenReady
    .then(() => fetch(fullUrl, requestOptions))
    .then(response =>{
        if(ENVIRONMENT_HELPER.isDev()) { console.log(response) }
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            if(ENVIRONMENT_HELPER.isDev()) { console.log(data) }
            if (!response.ok) {
                var errorMsg;
                switch(response.status)
                {
                    case 401:
                        {
                            errorMsg = "The token is not valid.";

                            store.dispatch('users/logout', null, { root: true });
                            break;
                        }
                    case 403:
                        {
                            // Logged in without the Admin role: species writes are refused (empty 403 body).
                            errorMsg = new String(i18n.t("forbiddenAction"));
                            break;
                        }
                    default:
                        {
                            errorMsg = data;
                            break;
                        }
                }

                return Promise.reject(errorMsg);
            }
    
            return data;
        });        
    })
    .catch((err) => 
    {
        if (err.name === 'AbortError') 
        {
            console.log("The request was aborted!");
        }
        else
        {
            if(err instanceof String)
            {
                return Promise.reject(err.toString());
            }
            else if(err && err.Errors)
            {
                let errorArray = [];
                for (var i = 0; i < Object.keys(err.Errors).length; i++) {
                    let key = Object.keys(err.Errors)[i];
                    errorArray[key] = err.Errors[key];
                } 

                return Promise.reject(errorArray);
            }
            else
            {
                return Promise.reject("Something went wrong, please try again. If the problem persists contact the support.");
            }
        }
    }); 
}

/* Handles 1) request cancellation 2) fetch url 3) error handler */
function apiCall(url, queryParams, requestOptions, canBeAborted)
{
    // Validate parameters
    if(!url || !requestOptions)
    {
        throw("The necessary info for the api call was not provided!");
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(requestOptions) }

    canBeAborted = (canBeAborted === undefined || canBeAborted === null) ? true : canBeAborted;

    // Build query parameters
    var fullUrl = encodeURI(url);
    if(queryParams)
    {
        fullUrl += "?";
        for(var key in queryParams) 
        {
            var value = queryParams[key];

            if(value || value == 0)
            {
                var encodedValue = encodeURIComponent(value);

                fullUrl += `&${key}=${encodedValue}`;
            }
        }
    }

    // Complete the request options adding signal so that the request can be aborted.
    if(canBeAborted)
    {
        requestOptions.signal = API_HELPER.abortController.signal;
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(fullUrl) }

    return fetch(fullUrl, requestOptions)
    .then(response =>{
        if(ENVIRONMENT_HELPER.isDev()) { console.log(response) }
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            if(ENVIRONMENT_HELPER.isDev()) { console.log(data) }
            if (!response.ok) {
                var errorMsg;

                if(typeof error != 'string')
                {
                    if(ENVIRONMENT_HELPER.isProd())
                    {
                        errorMsg = "Something went wrong, please try again. If the problem persists contact the support.";
                    }
                    else
                    {
                        errorMsg = error.toString();
                    }
                }
                else
                {
                    errorMsg = error;
                }
    
                return Promise.reject(errorMsg);
            }
    
            return data;
        });        
    })
    .catch((err) => 
    {
        if (err.name === 'AbortError') 
        {
            console.log("The request was aborted!");
        }
        else
        {
            var errorMsg;

            if(ENVIRONMENT_HELPER.isProd())
            {
                errorMsg = "Something went wrong, please try again. If the problem persists contact the support.";
            }
            else
            {
                errorMsg = err.toString();
            }
    
            return Promise.reject(errorMsg);
        }
    });
}